import React from "react";
import { Alert, Box, Switch, TextField, Typography } from "@mui/material";

const LATE_CHOICES = [5, 10, 15, 30, 60];
const messageOf = (err) => err?.response?.data?.message || "Couldn't save that setting. Try again.";

const eyebrow = {
  fontSize: 11, fontWeight: 600, letterSpacing: ".12em", textTransform: "uppercase", color: "#7b7389", m: 0, mb: 1.25,
};
const optRow = { display: "grid", gridTemplateColumns: "minmax(0,1fr) auto", gap: 2, alignItems: "center", p: 2, m: 0 };

/** One option row: a title, a hint and a switch, laid out as in the check-in design. */
function SwitchRow({ title, hint, extra, checked, disabled, onChange, divider }) {
  return (
    <Box component="label" sx={{ ...optRow, borderTop: divider ? "1px solid var(--color-hairline-cloud)" : 0, cursor: disabled ? "not-allowed" : "pointer" }}>
      <Box>
        <Typography component="span" sx={{ display: "block", fontWeight: 500, fontSize: 14, color: disabled ? "#a6a0b3" : "inherit" }}>{title}</Typography>
        <Typography component="span" sx={{ display: "block", fontSize: 13, color: "#7b7389" }}>{hint}</Typography>
        {extra}
      </Box>
      <Switch checked={checked} disabled={disabled} onChange={onChange} inputProps={{ "aria-label": title }}
        sx={{ "& .Mui-checked + .MuiSwitch-track": { bgcolor: "var(--color-primary)", opacity: 1 }, "& .MuiSwitch-switchBase.Mui-checked": { color: "#fff" } }} />
    </Box>
  );
}

/**
 * The check-in options for one session. Each change saves straight away, all three together (the server stores the
 * set as a whole), and a refused save puts the control back and says why. Picking names and strict location both
 * depend on the room's location, so they stay off until it has been set.
 */
export default function CheckinSettingsPanel({ settings, onSave }) {
  const [local, setLocal] = React.useState(settings);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState(null);

  React.useEffect(() => { setLocal(settings); }, [settings]);

  const change = async (patch) => {
    const previous = local;
    const next = { ...local, ...patch };
    setLocal(next);
    setSaving(true);
    setError(null);
    try {
      await onSave({
        rosterPickEnabled: !!next.rosterPickEnabled,
        strictLocation: !!next.strictLocation,
        lateAfterMinutes: next.lateAfterMinutes ?? null,
      });
    } catch (err) {
      setLocal(previous);
      setError(messageOf(err));
    } finally {
      setSaving(false);
    }
  };

  const needsRoom = !local.locationSet;

  return (
    <Box>
      <Typography component="h3" sx={eyebrow}>Options</Typography>
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

      <Box sx={{ border: "1px solid var(--color-hairline-cloud)", borderRadius: "12px", overflow: "hidden" }}>
        <SwitchRow
          title="Let students without an account pick their name"
          hint="They must be in the room. Names picked this way are saved as present and you'll review them below."
          checked={!!local.rosterPickEnabled} disabled={saving || needsRoom}
          onChange={(e) => change({ rosterPickEnabled: e.target.checked })}
        />
        <SwitchRow
          divider
          title="Only accept check-ins from inside the room"
          hint="Rejects scans from outside the room's location."
          extra={needsRoom && (
            <Typography component="span" sx={{ display: "block", mt: 0.5, fontSize: 12, color: "var(--color-warn)" }}>
              Set the room location first: stand in the room and use your location.
            </Typography>
          )}
          checked={!!local.strictLocation} disabled={saving || needsRoom}
          onChange={(e) => change({ strictLocation: e.target.checked })}
        />
        <Box sx={{ ...optRow, borderTop: "1px solid var(--color-hairline-cloud)" }}>
          <Box>
            <Typography component="span" sx={{ display: "block", fontWeight: 500, fontSize: 14 }}>Mark late after</Typography>
            <Typography component="span" sx={{ display: "block", fontSize: 13, color: "#7b7389" }}>
              Check-ins after this point from when the code is first shown are marked late.
            </Typography>
          </Box>
          <TextField
            select size="small" label="Mark late after" disabled={saving} sx={{ minWidth: 120 }}
            value={local.lateAfterMinutes ?? ""}
            onChange={(e) => change({ lateAfterMinutes: e.target.value === "" ? null : Number(e.target.value) })}
            SelectProps={{ native: true }} InputLabelProps={{ shrink: true }}
          >
            <option value="">Off</option>
            {LATE_CHOICES.map((m) => <option key={m} value={m}>{m} min</option>)}
          </TextField>
        </Box>
      </Box>
    </Box>
  );
}
